import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AxiosError } from 'axios';
import axios from 'axios';
import { Filter, QueryGet } from '../../shared/query-types';
import { Pagination } from '../../models/pagination.interface';
import { PAGINATION } from '../../shared/constants';
import { ToastrService } from 'ngx-toastr';

@Injectable({
    providedIn: 'root',
})
export class RequestService {
    constructor(
        private http: HttpClient,
        private router: Router,
        private toastr: ToastrService
    ) {}

    postAxios<T>(url: string, data: T) {
        return new Observable((observer) => {
            axios
                .post(url, data, { responseType: 'blob' })
                .then((response) => {
                    observer.next(response);
                    observer.complete();
                })
                .catch((error: AxiosError<Blob>) => {
                    if (error.response) {
                        // Si hay una respuesta del servidor
                        const contentType =
                            error.response.headers['content-type'];
                        if (
                            contentType &&
                            contentType.indexOf('application/json') !== -1
                        ) {
                            // Si la respuesta es de tipo JSON
                            error.response.data.text().then((msg) => {
                                observer.error(JSON.parse(msg));
                                observer.complete();
                            });
                        } else {
                            // Si la respuesta no es de tipo JSON
                            observer.error(
                                'Error: Respuesta no válida del servidor'
                            );
                            observer.complete();
                        }
                    } else {
                        // Si no hay respuesta del servidor
                        observer.error(
                            'Error: No se pudo conectar con el servidor'
                        );
                        observer.complete();
                    }
                });
        });
    }

    getAxios(url: string, queryParams: QueryGet) {
        const params = `search=${queryParams.search}&limit=${queryParams.limit}&offset=${queryParams.offset}&ordering=${queryParams.order}`;
        const filterFields = queryParams.filters.map((filter) => {
            return `${filter.name}=${filter.value}`;
        });
        const filterParams = this.genParams(filterFields);
        const urlWithParams =
            filterParams.length > 0
                ? `${url}?${params}&${filterParams}`
                : `${url}?${params}`;
        const token = localStorage.getItem('access_token');
        return new Observable((observer) => {
            axios
                .get(urlWithParams, { responseType: 'blob',
                     headers: {
                    Authorization: `Bearer ${token}`,
                },
                 })
                .then((response) => {
                    observer.next(response);
                    observer.complete();
                })
                .catch((error: AxiosError<Blob>) => {
                    if (error.response) {
                        const contentType =
                            error.response.headers['content-type'];
                        if (
                            contentType &&
                            contentType.indexOf('application/json') !== -1
                        ) {
                            // Si la respuesta es de tipo JSON
                            error.response.data.text().then((msg) => {
                                observer.error(JSON.parse(msg));
                                observer.complete();
                            });
                        } else {
                            // Si la respuesta no es de tipo JSON
                            observer.error(
                                'Error: Respuesta no válida del servidor'
                            );
                            observer.complete();
                        }
                    } else {
                        // Si no hay respuesta del servidor
                        observer.error(
                            'Error: No se pudo conectar con el servidor'
                        );
                        observer.complete();
                    }
                });
        });
    }

    /**
     * response handler for generic crud responses
     * @param recordName entity record name or id ex: 'sercretario'
     * @param redirectTo if reuest is success will redirect here by default is 'inicio
     * @param action text action will show on messge 'actulizado', 'eliminado' by default is 'creado'
     * @returns observable object handler
     * @author chatuz
     */
    resHandler(
        recordName: string,
        redirectTo: string = 'inicio',
        action: string = 'creado'
    ) {
        return {
            next: (data: unknown) => {
                this.router.navigate([redirectTo]);
                const msg = `El registro <b>${recordName}</b> fue ${action} <b>CORRECTAMENTE!</b>`;
                this.toastr.success(msg, '¡Operación exitosa!');
            },
            error: ({ error }: HttpErrorResponse) => {
                // Convierte el objeto de errores en un string amigable
                const msg = Object.entries(error)
                    .map(([key, val]) => `${key}: ${val}`)
                    .join('\n');
                this.toastr.error(msg, '¡Error en la operación!');
            },
        };
    }

    get<T>(url: string, queryParams: QueryGet): Observable<Pagination<T>> {
        const params = `search=${queryParams.search}&limit=${queryParams.limit}&offset=${queryParams.offset}&ordering=${queryParams.order}`;
        const filterFields = queryParams.filters.map((filter) => {
            return `${filter.name}=${filter.value}`;
        });
        const filterParams = this.genParams(filterFields);
        const urlWithParams =
            filterParams.length > 0
                ? `${url}?${params}&${filterParams}`
                : `${url}?${params}`;
        return this.http.get<Pagination<T>>(urlWithParams);
    }

    getAll<T>(url: string, queryParams: QueryGet): Observable<T> {
        const params = `search=${queryParams.search}&limit=${queryParams.limit}&offset=${queryParams.offset}&ordering=${queryParams.order}`;
        const filterFields = queryParams.filters.map((filter) => {
            return `${filter.name}=${filter.value}`;
        });
        const filterParams = this.genParams(filterFields);
        const urlWithParams =
            filterParams.length > 0
                ? `${url}?${params}&${filterParams}`
                : `${url}?${params}`;
        return this.http.get<T>(urlWithParams);
    }

    genParams(params: string[]): string {
        if (params.length === 0) {
            return '';
        }
        return `${params.join('&')}`;
    }

    genDefaultParams(filters: Filter[] = [], search = ''): QueryGet {
        return {
            limit: PAGINATION.page_number,
            order: '-id',
            offset: 0,
            filters,
            search,
        };
    }

    /** Detects if given url refers to an edition path */
    hasEditPath(url: string): boolean {
        const regex = /\/api\/v1\/\w+\/\d+\/$/;
        const urlToCheck = new URL(url);
        const path = urlToCheck.pathname;
        if (regex.test(path)) {
            return true;
        }

        return false;
}
}
